// @ts-nocheck
/**
 * A/B Test Configuration Component
 * Advanced test creation and variant management with Carbon Design System
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Button,
  ButtonSet,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  NumberInput,
  DatePicker,
  DatePickerInput,
  Toggle,
  Slider,
  CodeSnippet,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  Accordion,
  AccordionItem,
  Modal,
  Tag,
  Dropdown,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { Add, TrashCan, Copy, View, Code, Settings, Analytics, Information, WarningFilled } from '@carbon/icons-react';

interface ABTestConfig {
  id?: string;
  name: string;
  description: string;
  objective: string;
  hypothesis: string;
  successMetric: string;
  trafficAllocation: number;
  startDate: string;
  endDate?: string;
  targetAudience: TargetAudience;
  variants: TestVariant[];
  winnerCriteria: WinnerCriteria;
  advancedSettings: AdvancedSettings;
}

interface TestVariant {
  id: string;
  name: string;
  description: string;
  allocation: number;
  isControl: boolean;
  content: VariantContent;
}

interface VariantContent {
  type: 'html' | 'json' | 'url_redirect';
  data: any;
}

interface TargetAudience {
  include: AudienceRule[];
  exclude: AudienceRule[];
}

interface AudienceRule {
  field: string;
  operator: string;
  value: string;
}

interface WinnerCriteria {
  metric: string;
  minimumSampleSize: number;
  confidenceLevel: number;
  minimumDetectableDifference: number;
  testDuration: number;
  earlyStoppingEnabled: boolean;
}

interface AdvancedSettings {
  cookieDuration: number;
  qa_mode: boolean;
  preventFlicker: boolean;
  crossDomainTracking: boolean;
  customJavaScript: string;
  customCSS: string;
}

interface ABTestConfigurationProps {
  testId?: string;
  initialConfig?: ABTestConfig;
  onSave: (config: ABTestConfig) => void;
  onCancel: () => void;
}

const ABTestConfiguration: React.FC<ABTestConfigurationProps> = ({ testId, initialConfig, onSave, onCancel }) => {
  const [config, setConfig] = useState<ABTestConfig>(
    initialConfig || {
      name: '',
      description: '',
      objective: '',
      hypothesis: '',
      successMetric: 'conversion_rate',
      trafficAllocation: 100,
      startDate: '',
      targetAudience: {
        include: [],
        exclude: [],
      },
      variants: [
        {
          id: 'control',
          name: 'Control',
          description: 'Original version',
          allocation: 50,
          isControl: true,
          content: { type: 'html', data: '' },
        },
        {
          id: 'variant_a',
          name: 'Variant A',
          description: 'Test version',
          allocation: 50,
          isControl: false,
          content: { type: 'html', data: '' },
        },
      ],
      winnerCriteria: {
        metric: 'conversion_rate',
        minimumSampleSize: 1000,
        confidenceLevel: 95,
        minimumDetectableDifference: 5,
        testDuration: 14,
        earlyStoppingEnabled: true,
      },
      advancedSettings: {
        cookieDuration: 90,
        qa_mode: false,
        preventFlicker: true,
        crossDomainTracking: false,
        customJavaScript: '',
        customCSS: '',
      },
    }
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<TestVariant | null>(null);

  const steps = [
    { label: 'Basic Setup', description: 'Test name and objectives' },
    { label: 'Audience Targeting', description: 'Define target audience' },
    { label: 'Variant Configuration', description: 'Create test variants' },
    { label: 'Success Criteria', description: 'Define winner criteria' },
    { label: 'Advanced Settings', description: 'Technical configuration' },
    { label: 'Review & Launch', description: 'Final review' },
  ];

  const updateConfig = (updates: Partial<ABTestConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const addVariant = () => {
    const newVariant: TestVariant = {
      id: `variant_${Date.now()}`,
      name: `Variant ${String.fromCharCode(65 + config.variants.length)}`,
      description: '',
      allocation: 0,
      isControl: false,
      content: { type: 'html', data: '' },
    };

    updateConfig({
      variants: [...config.variants, newVariant],
    });
  };

  const removeVariant = (variantId: string) => {
    updateConfig({
      variants: config.variants.filter(v => v.id !== variantId),
    });
  };

  const updateVariant = (variantId: string, updates: Partial<TestVariant>) => {
    updateConfig({
      variants: config.variants.map(v => (v.id === variantId ? { ...v, ...updates } : v)),
    });
  };

  const redistributeTraffic = () => {
    const totalVariants = config.variants.length;
    const allocationPerVariant = Math.floor(100 / totalVariants);
    const remainder = 100 % totalVariants;

    const updatedVariants = config.variants.map((variant, index) => ({
      ...variant,
      allocation: allocationPerVariant + (index < remainder ? 1 : 0),
    }));

    updateConfig({ variants: updatedVariants });
  };

  const addAudienceRule = (type: 'include' | 'exclude') => {
    const newRule: AudienceRule = {
      field: 'url',
      operator: 'contains',
      value: '',
    };

    updateConfig({
      targetAudience: {
        ...config.targetAudience,
        [type]: [...config.targetAudience[type], newRule],
      },
    });
  };

  const updateAudienceRule = (type: 'include' | 'exclude', index: number, updates: Partial<AudienceRule>) => {
    const updatedRules = config.targetAudience[type].map((rule, i) => (i === index ? { ...rule, ...updates } : rule));

    updateConfig({
      targetAudience: {
        ...config.targetAudience,
        [type]: updatedRules,
      },
    });
  };

  const removeAudienceRule = (type: 'include' | 'exclude', index: number) => {
    updateConfig({
      targetAudience: {
        ...config.targetAudience,
        [type]: config.targetAudience[type].filter((_, i) => i !== index),
      },
    });
  };

  const validateConfig = (): string[] => {
    const errors: string[] = [];

    if (!config.name.trim()) {
      errors.push('Test name is required');
    }

    if (!config.objective.trim()) {
      errors.push('Test objective is required');
    }

    if (config.variants.length < 2) {
      errors.push('At least 2 variants are required');
    }

    const totalAllocation = config.variants.reduce((sum, variant) => sum + variant.allocation, 0);
    if (Math.abs(totalAllocation - 100) > 0.1) {
      errors.push('Variant allocations must sum to 100%');
    }

    const hasControl = config.variants.some(v => v.isControl);
    if (!hasControl) {
      errors.push('At least one variant must be marked as control');
    }

    if (config.winnerCriteria.minimumSampleSize < 100) {
      errors.push('Minimum sample size must be at least 100');
    }

    return errors;
  };

  const handleSave = () => {
    const errors = validateConfig();
    setValidationErrors(errors);

    if (errors.length === 0) {
      onSave(config);
    }
  };

  const renderBasicSetup = () => (
    <Grid>
      <Column lg={8}>
        <FormGroup legendText="Test Information">
          <TextInput
            id="test-name"
            labelText="Test Name"
            placeholder="Enter a descriptive name for your test"
            value={config.name}
            onChange={e => updateConfig({ name: e.target.value })}
            invalid={!config.name.trim()}
            invalidText="Test name is required"
          />

          <TextArea
            id="test-description"
            labelText="Description"
            placeholder="What are you testing and why?"
            value={config.description}
            onChange={e => updateConfig({ description: e.target.value })}
          />

          <TextArea
            id="test-objective"
            labelText="Test Objective"
            placeholder="What business goal are you trying to achieve?"
            value={config.objective}
            onChange={e => updateConfig({ objective: e.target.value })}
            invalid={!config.objective.trim()}
            invalidText="Test objective is required"
          />

          <TextArea
            id="test-hypothesis"
            labelText="Hypothesis"
            placeholder="What do you expect to happen and why?"
            value={config.hypothesis}
            onChange={e => updateConfig({ hypothesis: e.target.value })}
          />
        </FormGroup>

        <FormGroup legendText="Success Metrics">
          <Select
            id="success-metric"
            labelText="Primary Success Metric"
            value={config.successMetric}
            onChange={e => updateConfig({ successMetric: e.target.value })}
          >
            <SelectItem value="conversion_rate" text="Conversion Rate" />
            <SelectItem value="revenue_per_visitor" text="Revenue per Visitor" />
            <SelectItem value="click_through_rate" text="Click-through Rate" />
            <SelectItem value="bounce_rate" text="Bounce Rate" />
            <SelectItem value="time_on_page" text="Time on Page" />
            <SelectItem value="page_views" text="Page Views per Session" />
            <SelectItem value="signup_rate" text="Sign-up Rate" />
            <SelectItem value="engagement_score" text="Engagement Score" />
          </Select>
        </FormGroup>

        <FormGroup legendText="Test Duration">
          <DatePicker
            datePickerType="single"
            onChange={date => {
              if (date && date.length > 0) {
                updateConfig({ startDate: date[0].toISOString() });
              }
            }}
          >
            <DatePickerInput id="start-date" labelText="Start Date" placeholder="mm/dd/yyyy" />
          </DatePicker>

          <NumberInput
            id="test-duration"
            label="Test Duration (days)"
            min={1}
            max={90}
            value={config.winnerCriteria.testDuration}
            onChange={(e, { value }) =>
              updateConfig({
                winnerCriteria: { ...config.winnerCriteria, testDuration: value || 14 },
              })
            }
          />
        </FormGroup>
      </Column>

      <Column lg={8}>
        <Tile style={{ padding: '1.5rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Test Planning Tips</h4>
          <ul style={{ paddingLeft: '1rem', lineHeight: '1.6' }}>
            <li>Choose a clear, measurable objective that aligns with business goals</li>
            <li>Formulate a specific hypothesis about what will improve your metric</li>
            <li>Ensure you have enough traffic to reach statistical significance</li>
            <li>Plan to run the test for at least one business cycle (typically 1-2 weeks)</li>
            <li>Consider external factors that might influence results</li>
          </ul>
        </Tile>
      </Column>
    </Grid>
  );

  const renderAudienceTargeting = () => (
    <Grid>
      <Column lg={16}>
        <FormGroup legendText="Traffic Allocation">
          <Slider
            id="traffic-allocation"
            labelText={`Traffic Percentage: ${config.trafficAllocation}%`}
            min={1}
            max={100}
            value={config.trafficAllocation}
            onChange={data => updateConfig({ trafficAllocation: data.value })}
          />
          <p style={{ color: '#6f6f6f', fontSize: '0.875rem' }}>
            Percentage of visitors who will be included in the test
          </p>
        </FormGroup>

        <Accordion>
          <AccordionItem title="Include Rules (Who should see this test)">
            {config.targetAudience.include.map((rule, index) => (
              <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <Select
                  id={`include-field-${index}`}
                  labelText="Field"
                  value={rule.field}
                  onChange={e => updateAudienceRule('include', index, { field: e.target.value })}
                >
                  <SelectItem value="url" text="URL" />
                  <SelectItem value="referrer" text="Referrer" />
                  <SelectItem value="country" text="Country" />
                  <SelectItem value="device_type" text="Device Type" />
                  <SelectItem value="user_agent" text="User Agent" />
                  <SelectItem value="cookie" text="Cookie Value" />
                </Select>

                <Select
                  id={`include-operator-${index}`}
                  labelText="Operator"
                  value={rule.operator}
                  onChange={e => updateAudienceRule('include', index, { operator: e.target.value })}
                >
                  <SelectItem value="equals" text="Equals" />
                  <SelectItem value="contains" text="Contains" />
                  <SelectItem value="starts_with" text="Starts With" />
                  <SelectItem value="regex" text="Regex Match" />
                </Select>

                <TextInput
                  id={`include-value-${index}`}
                  labelText="Value"
                  value={rule.value}
                  onChange={e => updateAudienceRule('include', index, { value: e.target.value })}
                />

                <Button
                  kind="ghost"
                  renderIcon={TrashCan}
                  onClick={() => removeAudienceRule('include', index)}
                  iconDescription="Remove rule"
                />
              </div>
            ))}
            <Button kind="ghost" renderIcon={Add} onClick={() => addAudienceRule('include')}>
              Add Include Rule
            </Button>
          </AccordionItem>

          <AccordionItem title="Exclude Rules (Who should NOT see this test)">
            {config.targetAudience.exclude.map((rule, index) => (
              <div key={index} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <Select
                  id={`exclude-field-${index}`}
                  labelText="Field"
                  value={rule.field}
                  onChange={e => updateAudienceRule('exclude', index, { field: e.target.value })}
                >
                  <SelectItem value="url" text="URL" />
                  <SelectItem value="referrer" text="Referrer" />
                  <SelectItem value="country" text="Country" />
                  <SelectItem value="device_type" text="Device Type" />
                  <SelectItem value="user_agent" text="User Agent" />
                  <SelectItem value="cookie" text="Cookie Value" />
                </Select>

                <Select
                  id={`exclude-operator-${index}`}
                  labelText="Operator"
                  value={rule.operator}
                  onChange={e => updateAudienceRule('exclude', index, { operator: e.target.value })}
                >
                  <SelectItem value="equals" text="Equals" />
                  <SelectItem value="contains" text="Contains" />
                  <SelectItem value="starts_with" text="Starts With" />
                  <SelectItem value="regex" text="Regex Match" />
                </Select>

                <TextInput
                  id={`exclude-value-${index}`}
                  labelText="Value"
                  value={rule.value}
                  onChange={e => updateAudienceRule('exclude', index, { value: e.target.value })}
                />

                <Button
                  kind="ghost"
                  renderIcon={TrashCan}
                  onClick={() => removeAudienceRule('exclude', index)}
                  iconDescription="Remove rule"
                />
              </div>
            ))}
            <Button kind="ghost" renderIcon={Add} onClick={() => addAudienceRule('exclude')}>
              Add Exclude Rule
            </Button>
          </AccordionItem>
        </Accordion>
      </Column>
    </Grid>
  );

  const renderVariantConfiguration = () => (
    <Grid>
      <Column lg={16}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4>Test Variants</h4>
          <ButtonSet>
            <Button kind="secondary" onClick={redistributeTraffic}>
              Redistribute Traffic Equally
            </Button>
            <Button kind="primary" renderIcon={Add} onClick={addVariant}>
              Add Variant
            </Button>
          </ButtonSet>
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {config.variants.map((variant, index) => (
            <Tile key={variant.id} style={{ padding: '1.5rem' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <h5>{variant.name}</h5>
                    {variant.isControl && <Tag type="outline">Control</Tag>}
                  </div>

                  <div
                    style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '1rem', alignItems: 'end' }}
                  >
                    <TextInput
                      id={`variant-name-${variant.id}`}
                      labelText="Variant Name"
                      value={variant.name}
                      onChange={e => updateVariant(variant.id, { name: e.target.value })}
                    />

                    <TextInput
                      id={`variant-description-${variant.id}`}
                      labelText="Description"
                      value={variant.description}
                      onChange={e => updateVariant(variant.id, { description: e.target.value })}
                    />

                    <NumberInput
                      id={`variant-allocation-${variant.id}`}
                      label="Traffic %"
                      min={0}
                      max={100}
                      value={variant.allocation}
                      onChange={(e, { value }) => updateVariant(variant.id, { allocation: value || 0 })}
                    />
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <Toggle
                      id={`variant-control-${variant.id}`}
                      labelText="Control Variant"
                      toggled={variant.isControl}
                      onToggle={checked => {
                        // Ensure only one control variant
                        if (checked) {
                          const updatedVariants = config.variants.map(v => ({
                            ...v,
                            isControl: v.id === variant.id,
                          }));
                          updateConfig({ variants: updatedVariants });
                        } else {
                          updateVariant(variant.id, { isControl: false });
                        }
                      }}
                    />
                  </div>
                </div>

                <ButtonSet>
                  <Button kind="ghost" size="sm" renderIcon={View} onClick={() => setSelectedVariant(variant)}>
                    Edit Content
                  </Button>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Copy}
                    onClick={() => {
                      const copiedVariant: TestVariant = {
                        ...variant,
                        id: `variant_${Date.now()}`,
                        name: `${variant.name} Copy`,
                        isControl: false,
                      };
                      updateConfig({ variants: [...config.variants, copiedVariant] });
                    }}
                  >
                    Duplicate
                  </Button>
                  {config.variants.length > 2 && (
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      onClick={() => removeVariant(variant.id)}
                    >
                      Remove
                    </Button>
                  )}
                </ButtonSet>
              </div>
            </Tile>
          ))}
        </div>

        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f4f4f4', borderRadius: '8px' }}>
          <p>
            <strong>Total Traffic Allocation:</strong> {config.variants.reduce((sum, v) => sum + v.allocation, 0)}%
          </p>
        </div>
      </Column>
    </Grid>
  );

  const renderSuccessCriteria = () => (
    <Grid>
      <Column lg={8}>
        <FormGroup legendText="Statistical Settings">
          <NumberInput
            id="min-sample-size"
            label="Minimum Sample Size"
            helperText="Minimum number of conversions needed per variant"
            min={100}
            max={100000}
            value={config.winnerCriteria.minimumSampleSize}
            onChange={(e, { value }) =>
              updateConfig({
                winnerCriteria: { ...config.winnerCriteria, minimumSampleSize: value || 1000 },
              })
            }
          />

          <NumberInput
            id="confidence-level"
            label="Confidence Level (%)"
            helperText="Statistical confidence required for winner selection"
            min={80}
            max={99}
            value={config.winnerCriteria.confidenceLevel}
            onChange={(e, { value }) =>
              updateConfig({
                winnerCriteria: { ...config.winnerCriteria, confidenceLevel: value || 95 },
              })
            }
          />

          <NumberInput
            id="min-detectable-diff"
            label="Minimum Detectable Difference (%)"
            helperText="Smallest improvement that would be practically significant"
            min={1}
            max={50}
            value={config.winnerCriteria.minimumDetectableDifference}
            onChange={(e, { value }) =>
              updateConfig({
                winnerCriteria: { ...config.winnerCriteria, minimumDetectableDifference: value || 5 },
              })
            }
          />

          <Toggle
            id="early-stopping"
            labelText="Enable Early Stopping"
            helperText="Allow test to conclude early if statistical significance is reached"
            toggled={config.winnerCriteria.earlyStoppingEnabled}
            onToggle={checked =>
              updateConfig({
                winnerCriteria: { ...config.winnerCriteria, earlyStoppingEnabled: checked },
              })
            }
          />
        </FormGroup>
      </Column>

      <Column lg={8}>
        <Tile style={{ padding: '1.5rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Statistical Power Calculator</h4>
          <p style={{ color: '#6f6f6f', marginBottom: '1rem' }}>Based on your current settings:</p>
          <ul style={{ paddingLeft: '1rem', lineHeight: '1.6', color: '#6f6f6f' }}>
            <li>
              Minimum test duration: ~
              {Math.ceil((config.winnerCriteria.minimumSampleSize * config.variants.length) / 100)} days
            </li>
            <li>Statistical power: 80%</li>
            <li>Type I error rate: {100 - config.winnerCriteria.confidenceLevel}%</li>
            <li>Detectable improvement: {config.winnerCriteria.minimumDetectableDifference}%+</li>
          </ul>
        </Tile>
      </Column>
    </Grid>
  );

  const renderAdvancedSettings = () => (
    <Grid>
      <Column lg={8}>
        <FormGroup legendText="Technical Settings">
          <NumberInput
            id="cookie-duration"
            label="Cookie Duration (days)"
            helperText="How long visitors stay in their assigned variant"
            min={1}
            max={365}
            value={config.advancedSettings.cookieDuration}
            onChange={(e, { value }) =>
              updateConfig({
                advancedSettings: { ...config.advancedSettings, cookieDuration: value || 90 },
              })
            }
          />

          <Toggle
            id="qa-mode"
            labelText="QA Mode"
            helperText="Allow testing with URL parameters"
            toggled={config.advancedSettings.qa_mode}
            onToggle={checked =>
              updateConfig({
                advancedSettings: { ...config.advancedSettings, qa_mode: checked },
              })
            }
          />

          <Toggle
            id="prevent-flicker"
            labelText="Prevent Flicker"
            helperText="Hide content until variant is determined"
            toggled={config.advancedSettings.preventFlicker}
            onToggle={checked =>
              updateConfig({
                advancedSettings: { ...config.advancedSettings, preventFlicker: checked },
              })
            }
          />

          <Toggle
            id="cross-domain"
            labelText="Cross-domain Tracking"
            helperText="Track visitors across subdomains"
            toggled={config.advancedSettings.crossDomainTracking}
            onToggle={checked =>
              updateConfig({
                advancedSettings: { ...config.advancedSettings, crossDomainTracking: checked },
              })
            }
          />
        </FormGroup>
      </Column>

      <Column lg={8}>
        <FormGroup legendText="Custom Code">
          <TextArea
            id="custom-javascript"
            labelText="Custom JavaScript"
            placeholder="// Enter custom JavaScript code here"
            value={config.advancedSettings.customJavaScript}
            onChange={e =>
              updateConfig({
                advancedSettings: { ...config.advancedSettings, customJavaScript: e.target.value },
              })
            }
          />

          <TextArea
            id="custom-css"
            labelText="Custom CSS"
            placeholder="/* Enter custom CSS here */"
            value={config.advancedSettings.customCSS}
            onChange={e =>
              updateConfig({
                advancedSettings: { ...config.advancedSettings, customCSS: e.target.value },
              })
            }
          />
        </FormGroup>
      </Column>
    </Grid>
  );

  const renderReviewLaunch = () => (
    <Grid>
      <Column lg={16}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Configuration Summary */}
          <Tile style={{ padding: '1.5rem' }}>
            <h4>Test Summary</h4>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem',
                marginTop: '1rem',
              }}
            >
              <div>
                <p>
                  <strong>Name:</strong> {config.name}
                </p>
                <p>
                  <strong>Objective:</strong> {config.objective}
                </p>
                <p>
                  <strong>Success Metric:</strong> {config.successMetric}
                </p>
              </div>
              <div>
                <p>
                  <strong>Traffic Allocation:</strong> {config.trafficAllocation}%
                </p>
                <p>
                  <strong>Variants:</strong> {config.variants.length}
                </p>
                <p>
                  <strong>Min Sample Size:</strong> {config.winnerCriteria.minimumSampleSize.toLocaleString()}
                </p>
              </div>
              <div>
                <p>
                  <strong>Confidence Level:</strong> {config.winnerCriteria.confidenceLevel}%
                </p>
                <p>
                  <strong>Test Duration:</strong> {config.winnerCriteria.testDuration} days
                </p>
                <p>
                  <strong>Early Stopping:</strong> {config.winnerCriteria.earlyStoppingEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
          </Tile>

          {/* Variant Summary */}
          <Tile style={{ padding: '1.5rem' }}>
            <h4>Variant Configuration</h4>
            <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
              {config.variants.map(variant => (
                <div
                  key={variant.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: variant.isControl ? '#e8f5e8' : '#f4f4f4',
                    borderRadius: '4px',
                  }}
                >
                  <div>
                    <strong>{variant.name}</strong> {variant.isControl && <Tag type="outline">Control</Tag>}
                    <p style={{ color: '#6f6f6f', margin: '0.25rem 0 0 0' }}>{variant.description}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <strong>{variant.allocation}%</strong>
                  </div>
                </div>
              ))}
            </div>
          </Tile>

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <InlineNotification
              kind="error"
              title="Configuration Issues"
              subtitle="Please fix the following issues before launching:"
            >
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1rem' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </InlineNotification>
          )}

          {/* Pre-launch Checklist */}
          <Tile style={{ padding: '1.5rem' }}>
            <h4>Pre-launch Checklist</h4>
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <input type="checkbox" id="checklist-1" />
                <label htmlFor="checklist-1" style={{ marginLeft: '0.5rem' }}>
                  Test objective is clearly defined and measurable
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <input type="checkbox" id="checklist-2" />
                <label htmlFor="checklist-2" style={{ marginLeft: '0.5rem' }}>
                  Variants have been reviewed and approved
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <input type="checkbox" id="checklist-3" />
                <label htmlFor="checklist-3" style={{ marginLeft: '0.5rem' }}>
                  Success metrics and criteria are appropriate
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <input type="checkbox" id="checklist-4" />
                <label htmlFor="checklist-4" style={{ marginLeft: '0.5rem' }}>
                  Target audience settings have been verified
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <input type="checkbox" id="checklist-5" />
                <label htmlFor="checklist-5" style={{ marginLeft: '0.5rem' }}>
                  Stakeholders have been notified of test launch
                </label>
              </div>
            </div>
          </Tile>
        </div>
      </Column>
    </Grid>
  );

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f4f4f4', minHeight: '100vh' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '600', color: '#0f62fe', margin: 0 }}>
            {testId ? 'Edit A/B Test' : 'Create A/B Test'}
          </h1>
          <p style={{ color: '#6f6f6f', margin: '0.5rem 0 0 0' }}>{steps[currentStep].description}</p>
        </div>
        <ButtonSet>
          <Button kind="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button kind="primary" onClick={handleSave} style={{ backgroundColor: '#0f62fe' }}>
            {testId ? 'Save Changes' : 'Create Test'}
          </Button>
        </ButtonSet>
      </div>

      {/* Progress Indicator */}
      <div style={{ marginBottom: '2rem', backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px' }}>
        <ProgressIndicator currentIndex={currentStep} spaceEqually>
          {steps.map((step, index) => (
            <ProgressStep
              key={index}
              label={step.label}
              description={step.description}
              onClick={() => setCurrentStep(index)}
            />
          ))}
        </ProgressIndicator>
      </div>

      {/* Step Content */}
      <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', marginBottom: '2rem' }}>
        {currentStep === 0 && renderBasicSetup()}
        {currentStep === 1 && renderAudienceTargeting()}
        {currentStep === 2 && renderVariantConfiguration()}
        {currentStep === 3 && renderSuccessCriteria()}
        {currentStep === 4 && renderAdvancedSettings()}
        {currentStep === 5 && renderReviewLaunch()}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button kind="secondary" disabled={currentStep === 0} onClick={() => setCurrentStep(currentStep - 1)}>
          Previous
        </Button>
        <Button
          kind="primary"
          disabled={currentStep === steps.length - 1}
          onClick={() => setCurrentStep(currentStep + 1)}
          style={{ backgroundColor: '#0f62fe' }}
        >
          Next
        </Button>
      </div>

      {/* Variant Content Editor Modal */}
      {selectedVariant && (
        <Modal
          open={!!selectedVariant}
          onRequestClose={() => setSelectedVariant(null)}
          modalHeading={`Edit Content: ${selectedVariant.name}`}
          primaryButtonText="Save Content"
          secondaryButtonText="Cancel"
          onRequestSubmit={() => {
            // Update variant content
            setSelectedVariant(null);
          }}
          size="lg"
        >
          <Tabs>
            <TabList>
              <Tab>HTML Content</Tab>
              <Tab>JSON Data</Tab>
              <Tab>URL Redirect</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <TextArea
                  id="html-content"
                  labelText="HTML Content"
                  placeholder="Enter HTML content for this variant"
                  rows={10}
                  value={selectedVariant.content.type === 'html' ? selectedVariant.content.data : ''}
                  onChange={e => {
                    const updatedVariant = {
                      ...selectedVariant,
                      content: { type: 'html' as const, data: e.target.value },
                    };
                    updateVariant(selectedVariant.id, { content: updatedVariant.content });
                    setSelectedVariant(updatedVariant);
                  }}
                />
              </TabPanel>
              <TabPanel>
                <TextArea
                  id="json-content"
                  labelText="JSON Configuration"
                  placeholder="Enter JSON configuration for this variant"
                  rows={10}
                  value={
                    selectedVariant.content.type === 'json'
                      ? JSON.stringify(selectedVariant.content.data, null, 2)
                      : '{}'
                  }
                  onChange={e => {
                    try {
                      const jsonData = JSON.parse(e.target.value);
                      const updatedVariant = {
                        ...selectedVariant,
                        content: { type: 'json' as const, data: jsonData },
                      };
                      updateVariant(selectedVariant.id, { content: updatedVariant.content });
                      setSelectedVariant(updatedVariant);
                    } catch (error) {
                      // Invalid JSON - keep the text but don't update the variant yet
                    }
                  }}
                />
              </TabPanel>
              <TabPanel>
                <TextInput
                  id="redirect-url"
                  labelText="Redirect URL"
                  placeholder="https://example.com/landing-page"
                  value={selectedVariant.content.type === 'url_redirect' ? selectedVariant.content.data : ''}
                  onChange={e => {
                    const updatedVariant = {
                      ...selectedVariant,
                      content: { type: 'url_redirect' as const, data: e.target.value },
                    };
                    updateVariant(selectedVariant.id, { content: updatedVariant.content });
                    setSelectedVariant(updatedVariant);
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Modal>
      )}
    </div>
  );
};

export default ABTestConfiguration;
